import EditOutlined from '@mui/icons-material/EditOutlined';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { getUserDescription, updateUserDescription } from '../../firebase/users.utils';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectTripsList } from '../../redux/trip/trip.selectors';
import { fetchTrips } from '../../redux/trip/trip.slice';
import { selectCurrentUser, selectIsAuthInitialized } from '../../redux/user/user.selectors';
import { PLACEHOLDER_DISPLAY_NAME } from '../../utils/accountConstants';
import { countCountries, getTripMarkers } from '../../utils/profile.utils';
import GoogleMap from '../Map/Map';
import Spinner from '../Spinner/Spinner';
import { IconButton } from '../shared';
import CreateTripModal from '../TripsList/components/CreateTripModal/CreateTripModal';
import ProfileDescription from './ProfileDescription';
import './Profile.scss';

function Profile() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const isAuthInitialized = useAppSelector(selectIsAuthInitialized);
  const trips = useAppSelector(selectTripsList);
  const isMobile = useMobileMediaQuery();
  const [description, setDescription] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  const userId = currentUser?.uid;

  useEffect(() => {
    if (isAuthInitialized && !currentUser) {
      navigate('/login');
    }
  }, [isAuthInitialized, currentUser, navigate]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchTrips(userId));
      getUserDescription(userId)
        .then(setDescription)
        .catch(() => undefined);
    }
  }, [userId, dispatch]);

  const handleSaveDescription = useCallback(
    (next: string) => {
      if (!userId) {
        return;
      }
      const previous = description;
      setDescription(next);
      // Optimistic: roll back if the write fails.
      updateUserDescription(userId, next).catch(() => setDescription(previous));
    },
    [userId, description]
  );

  const markers = useMemo(() => getTripMarkers(trips ?? []), [trips]);
  const countryCount = useMemo(() => countCountries(trips ?? []), [trips]);

  if (!isAuthInitialized || !trips) {
    return <Spinner />;
  }

  const isEmpty = trips.length === 0;
  const name =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || PLACEHOLDER_DISPLAY_NAME;

  return (
    <div className='profile'>
      <Helmet>
        <title>Wander | Profile</title>
      </Helmet>
      <div className='profile-header'>
        <h1 className='profile-name'>{name}</h1>
        {/* Double click isn't dependable on touch screens, so mobile gets an explicit button. */}
        {isMobile && !isEditingDescription && (
          <IconButton
            className='profile-edit-button'
            label='Edit description'
            icon={<EditOutlined />}
            shape='circle'
            tooltip={false}
            onClick={() => setIsEditingDescription(true)}
          />
        )}
      </div>
      <ProfileDescription
        value={description}
        onSave={handleSaveDescription}
        isEditing={isEditingDescription}
        onEditingChange={setIsEditingDescription}
      />
      <h2 className='profile-section-title'>My Adventures</h2>
      <div className='profile-stats'>
        <span className='profile-stat'>
          Trips Tracked <strong>{trips.length}</strong>
        </span>
        <span className='profile-stat'>
          Countries Visited <strong>{countryCount}</strong>
        </span>
      </div>
      <div className='profile-map'>
        <div
          className={`profile-map-canvas${isEmpty ? ' disabled' : ''}`}
          aria-hidden={isEmpty}
        >
          <GoogleMap
            markers={markers}
            showCurrentLocation={false}
            autoFitMarkers
            center={{ lat: 25, lng: 10 }}
            zoom={2}
          />
        </div>
        {isEmpty && (
          <div className='profile-empty-state'>
            <div className='profile-empty-icon'>🏔️</div>
            <h3 className='profile-empty-title'>Get Started</h3>
            <p className='profile-empty-text'>
              Add your first trip to start tracking your journey around the world
            </p>
            <Button
              className='profile-empty-button'
              variant='outlined'
              onClick={() => navigate('/')}
            >
              Create Your First Trip
            </Button>
          </div>
        )}
      </div>
      {!isEmpty && <CreateTripModal />}
    </div>
  );
}

Profile.displayName = 'Profile';
export default Profile;
